import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { PersonaConfirmationProps } from './types';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { logger } from '../../utils/logger';

const ConfirmationContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const PersonaHeader = styled.div`
  background: ${theme.colors.gradients.primary};
  color: ${theme.colors.text.light};
  padding: 2rem;
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: 2rem;
`;

const PersonaIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const PersonaName = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PersonaTagline = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: ${theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 1rem;
  line-height: 1.6;
`;

const QuestionsContainer = styled.div`
  margin-bottom: 3rem;
`;

const QuestionCard = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: ${theme.shadows.light};
`;

const QuestionText = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  line-height: 1.4;
`;

const OptionsContainer = styled.div`
  display: grid;
  gap: 1rem;
`;

const OptionButton = styled.button<{ selected: boolean }>`
  background: ${props => props.selected
    ? theme.colors.gradients.primary
    : theme.colors.background.secondary};
  color: ${props => props.selected ? theme.colors.text.light : theme.colors.text.primary};
  border: 2px solid ${props => props.selected ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem 1.5rem;
  text-align: left;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  font-size: 1rem;
  line-height: 1.4;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
    border-color: ${props => props.selected ? theme.colors.primary : theme.colors.border.medium};
  }
`;

const OptionIcon = styled.span`
  margin-right: 0.75rem;
  font-size: 1.2rem;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' | 'outline' }>`
  padding: 1rem 2rem;
  border-radius: ${theme.borderRadius.lg};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  border: none;
  min-width: 140px;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${theme.colors.gradients.primary};
          color: ${theme.colors.text.light};
          box-shadow: ${theme.shadows.medium};
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.heavy};
            background: ${theme.colors.hover.primary};
          }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.text.secondary};
          color: ${theme.colors.text.light};
          
          &:hover {
            background: ${theme.colors.text.muted};
            transform: translateY(-2px);
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.text.secondary};
          border: 2px solid ${theme.colors.border.medium};
          
          &:hover {
            border-color: ${theme.colors.primary};
            color: ${theme.colors.primary};
            transform: translateY(-2px);
          }
        `;
      default:
        return '';
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const SpinnerWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.5rem;
`;

const ProgressIndicator = styled.div`
  background: ${theme.colors.border.light};
  border-radius: 10px;
  height: 8px;
  margin-bottom: 2rem;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ progress: number }>`
  background: ${theme.colors.gradients.primary};
  height: 100%;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

export const PersonaConfirmation: React.FC<PersonaConfirmationProps> = ({
  persona,
  questions,
  onAnswer,
  onNext,
  onBack,
  onSkip,
  loading = false,
  responses,
  hideNavigation = false
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [allAnswered, setAllAnswered] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  useEffect(() => {
    const answeredCount = Object.keys(responses).length;
    setAllAnswered(answeredCount === questions.length);
  }, [responses, questions.length]);

  const handleAnswer = (questionId: string, answer: string) => {
    const currentQuestion = questions[currentQuestionIndex];
    const selectedOption = currentQuestion.options.find(opt => opt.value === answer);

    logger.log('PersonaConfirmation: Answer selected', {
      questionId,
      answer,
      currentQuestionIndex,
      questionText: currentQuestion.question,
      answerLabel: selectedOption?.label || 'Unknown',
      answerIcon: selectedOption?.icon || 'None'
    });

    onAnswer(questionId, answer);

    // Auto-advance to next question after a short delay
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    }, 500);
  };

  const handleNext = () => {
    if (allAnswered) {
      onNext();
    } else {
      // Move to next unanswered question
      const nextIndex = questions.findIndex(q => !responses[q.id]);
      if (nextIndex !== -1) {
        setCurrentQuestionIndex(nextIndex);
      }
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else {
      onBack();
    }
  };

  return (
    <ConfirmationContainer>
      {!hideNavigation && (
        <Header>
          <PersonaHeader>
            <PersonaIcon>{persona.visualIdentity.imagery[0]}</PersonaIcon>
            <PersonaName>{persona.name}</PersonaName>
            <PersonaTagline>{persona.tagline}</PersonaTagline>
          </PersonaHeader>

          <Title>Confirm Your Persona</Title>
          <Subtitle>
            Answer a few quick questions to confirm this persona fits you.
            This helps us create better matches!
          </Subtitle>
        </Header>
      )}

      <ProgressIndicator>
        <ProgressBar progress={progress} />
      </ProgressIndicator>

      <QuestionsContainer>
        {currentQuestion && (
          <QuestionCard>
            <QuestionText>
              {currentQuestion.question}
            </QuestionText>

            <OptionsContainer>
              {currentQuestion.options.map((option, _index) => (
                <OptionButton
                  key={option.value}
                  selected={responses[currentQuestion.id] === option.value}
                  onClick={() => handleAnswer(currentQuestion.id, option.value)}
                  disabled={loading}
                >
                  {option.icon && <OptionIcon>{option.icon}</OptionIcon>}
                  {option.label}
                </OptionButton>
              ))}
            </OptionsContainer>
          </QuestionCard>
        )}
      </QuestionsContainer>

      {!hideNavigation && (
        <ActionButtons>
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </Button>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={loading}
            >
              Skip Questions
            </Button>

            <Button
              variant="primary"
              onClick={handleNext}
              disabled={loading || !allAnswered}
            >
              {loading && (
                <SpinnerWrapper>
                  <LoadingSpinner size="small" color={theme.colors.text.light} />
                </SpinnerWrapper>
              )}
              {allAnswered ? 'Complete' : 'Next Question'}
            </Button>
          </div>
        </ActionButtons>
      )}
    </ConfirmationContainer>
  );
};
